"use client";

import React, { useState } from "react";

import { getStockLogoUrl } from "@lattice/core-lib/utils/stock-logo";

import { ListGroup, ListRow } from "@lattice/frontend/components/kit/List";

import {
  formatCurrency,
  formatPositionPercent,
  formatShares,
  formatSignedPercent,
} from "./format";
import type { T_PortfolioHomeHolding } from "./types";
import "./portfolio-home.css";

export type T_HoldingsSectionProps = {
  defaultExpanded?: boolean;
  holdings: T_PortfolioHomeHolding[];
};

function getLogoUri(image: string | null): string | null {
  if (!image) return null;
  if (image.startsWith("http") || image.startsWith("data:")) return image;
  return `data:image/png;base64,${image}`;
}

function HoldingAvatar({ holding }: { holding: T_PortfolioHomeHolding }) {
  const logoUri =
    getLogoUri(holding.logoImage) ?? getStockLogoUrl(holding.symbol);
  return (
    <span className="portfolio-holding__avatar">
      <img
        alt=""
        className="portfolio-holding__logo"
        onError={(event) => {
          event.currentTarget.hidden = true;
        }}
        src={logoUri}
      />
      <span aria-hidden className="portfolio-holding__letter">
        {holding.symbol.trim().slice(0, 1)}
      </span>
    </span>
  );
}

function HoldingTrailing({ holding }: { holding: T_PortfolioHomeHolding }) {
  const change = holding.dailyPercentChange;
  return (
    <span className="portfolio-holding__trailing">
      <span className="portfolio-holding__value">
        {formatCurrency(holding.totalValue)}
      </span>
      <span
        className={
          change === null
            ? "portfolio-holding__change"
            : change >= 0
              ? "portfolio-holding__change portfolio-holding__change--positive"
              : "portfolio-holding__change portfolio-holding__change--negative"
        }
      >
        {change === null ? "—" : formatSignedPercent(change)}
      </span>
    </span>
  );
}

export function HoldingsSection({
  defaultExpanded = false,
  holdings,
}: T_HoldingsSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  return (
    <section className="portfolio-holdings">
      <button
        aria-expanded={expanded}
        className="portfolio-holdings__toggle"
        onClick={() => setExpanded((current) => !current)}
        type="button"
      >
        {expanded ? "Hide" : "View all holdings"}
      </button>
      <div
        className={
          expanded
            ? "portfolio-holdings__content portfolio-holdings__content--expanded"
            : "portfolio-holdings__content"
        }
      >
        {holdings.length > 0 ? (
          <ListGroup>
            {holdings.map((holding) => (
              <ListRow
                key={holding.symbol}
                leading={<HoldingAvatar holding={holding} />}
                subtitle={`${formatShares(holding.units)} · ${formatPositionPercent(holding.weightPercent)}`}
                title={holding.symbol}
                trailing={<HoldingTrailing holding={holding} />}
              />
            ))}
          </ListGroup>
        ) : (
          <p className="portfolio-holdings__empty">No invested holdings yet.</p>
        )}
      </div>
    </section>
  );
}
